import { useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Settings, ChevronRight, Layers, FileText, AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { examConfigApi, ExamConfig, ExamLevel, ExamSection } from "@/api/admin/examConfig";
import { useAuth } from "@/auth/AuthContext";
import ReactQuill from "react-quill";
// Import the Snow theme CSS from `quill` directly. The `react-quill/dist/...`
// subpath fails to resolve under Vite/Rollup production builds; `quill` (the
// underlying package) exposes the identical stylesheet reliably.
import "quill/dist/quill.snow.css";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    ["blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

export default function AdminExamConfigPage() {
  useAuth();
  const queryClient = useQueryClient();

  const { data: configs = [], isLoading } = useQuery({
    queryKey: ["adminExamConfigs"],
    queryFn: () => examConfigApi.getAll(),
  });

  const [activeConfig, setActiveConfig] = useState<ExamConfig | null>(null);

  const [editConfig, setEditConfig] = useState<Partial<ExamConfig> | null>(null);
  const [editLevel, setEditLevel] = useState<Partial<ExamLevel> | null>(null);
  const [editSection, setEditSection] = useState<Partial<ExamSection> | null>(null);

  const [deleteUsage, setDeleteUsage] = useState<{ type: 'CONFIG' | 'LEVEL' | 'SECTION', item: any, usage: any } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);


  const configMutation = useMutation({
    mutationFn: async (data: Partial<ExamConfig>) => {
      if (data.id) return examConfigApi.updateConfig(data.id, data);
      return examConfigApi.createConfig(data);
    },
    onSuccess: () => {
      toast.success("Exam configuration saved");
      queryClient.invalidateQueries({ queryKey: ["adminExamConfigs"] });
      setEditConfig(null);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || err.message || "Failed to save config"),
  });

  const deleteConfigMutation = useMutation({
    mutationFn: examConfigApi.deleteConfig,
    onSuccess: () => {
      toast.success("Exam configuration deleted");
      queryClient.invalidateQueries({ queryKey: ["adminExamConfigs"] });
    },
    onError: (err: any) => toast.error(err.response?.data?.message || err.message || "Failed to delete config"),
  });

  const levelMutation = useMutation({
    mutationFn: async (data: Partial<ExamLevel>) => {
      if (data.id) return examConfigApi.updateLevel(data.id, data);
      return examConfigApi.createLevel({ ...data, examConfigId: activeConfig!.id });
    },
    onSuccess: () => {
      toast.success("Level saved");
      queryClient.invalidateQueries({ queryKey: ["adminExamConfigs"] });
      setEditLevel(null);
    },
  });

  const sectionMutation = useMutation({
    mutationFn: async (data: Partial<ExamSection>) => {
      if (data.id) return examConfigApi.updateSection(data.id, data);
      return examConfigApi.createSection({ ...data, examConfigId: activeConfig!.id });
    },
    onSuccess: () => {
      toast.success("Section saved");
      queryClient.invalidateQueries({ queryKey: ["adminExamConfigs"] });
      setEditSection(null);
    },
  });


  const handleCheckDelete = async (type: 'CONFIG' | 'LEVEL' | 'SECTION', item: any) => {
    setIsDeleting(true);
    try {
      let usage;
      if (type === 'CONFIG') usage = await examConfigApi.getExamTypeUsage(item.id);
      if (type === 'LEVEL') usage = await examConfigApi.getLevelUsage(item.id);
      if (type === 'SECTION') usage = await examConfigApi.getSectionUsage(item.id);
      
      setDeleteUsage({ type, item, usage });
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || "Failed to fetch usage");
    } finally {
      setIsDeleting(false);
    }
  };

  const executeDelete = async () => {
    if (!deleteUsage) return;
    setIsDeleting(true);
    try {
      if (deleteUsage.type === 'CONFIG') await examConfigApi.deleteConfig(deleteUsage.item.id);
      if (deleteUsage.type === 'LEVEL') await examConfigApi.deleteLevel(deleteUsage.item.id);
      if (deleteUsage.type === 'SECTION') await examConfigApi.deleteSection(deleteUsage.item.id);
      
      toast.success("Successfully deleted");
      queryClient.invalidateQueries({ queryKey: ["adminExamConfigs"] });
      setDeleteUsage(null);
      if (deleteUsage.type === 'CONFIG' && deleteUsage.item.id === editConfig?.id) setEditConfig(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || "Failed to delete");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeactivateArchive = async (status: 'INACTIVE' | 'ARCHIVED') => {
    if (!deleteUsage) return;
    setIsDeleting(true);
    try {
      if (deleteUsage.type === 'CONFIG') await examConfigApi.updateConfig(deleteUsage.item.id, { status });
      if (deleteUsage.type === 'LEVEL') await examConfigApi.updateLevel(deleteUsage.item.id, { status });
      if (deleteUsage.type === 'SECTION') await examConfigApi.updateSection(deleteUsage.item.id, { status });
      
      toast.success(`Status updated to ${status}`);
      queryClient.invalidateQueries({ queryKey: ["adminExamConfigs"] });
      setDeleteUsage(null);
      if (deleteUsage.type === 'CONFIG' && deleteUsage.item.id === editConfig?.id) setEditConfig(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || "Failed to update status");
    } finally {
      setIsDeleting(false);
    }
  };

  // Re-sync activeConfig after refetch
  if (activeConfig) {
    const updated = configs.find((c) => c.id === activeConfig.id);
    if (updated && updated !== activeConfig) {
      setActiveConfig(updated);
    }
  }

  if (activeConfig) {
    return (
      <DashboardLayout>
        <div className="dash-page max-w-5xl mx-auto">
          <div className="mb-8">
            <button onClick={() => setActiveConfig(null)} className="text-sm text-primary hover:underline mb-2 flex items-center gap-1">
              &larr; Back to Exam Configurations
            </button>
            <h1 className="dash-page-title">{activeConfig.name} ({activeConfig.code})</h1>
            <p className="dash-page-subtitle">{activeConfig.description}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <KCard className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold flex items-center gap-2"><Layers size={18} /> Levels</h3>
                <Button size="sm" onClick={() => setEditLevel({ status: 'ACTIVE', sortOrder: (activeConfig.levels?.length || 0) + 1 })}>
                  <Plus size={14} className="mr-1" /> Add Level
                </Button>
              </div>
              <div className="space-y-2">
                {activeConfig.levels?.sort((a, b) => a.sortOrder - b.sortOrder).map(lvl => (
                  <div key={lvl.id} className="flex items-center justify-between p-3 border border-border rounded-lg bg-card">
                    <div>
                      <p className="font-medium text-sm">{lvl.name} <span className="text-muted-foreground text-xs">({lvl.code})</span></p>
                    </div>
                    <div className="flex gap-1"><Button variant="ghost" size="sm" onClick={() => setEditLevel(lvl)}><Pencil size={14} /></Button><Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleCheckDelete('LEVEL', lvl)}><Trash2 size={14} /></Button></div>
                  </div>
                ))}
              </div>
            </KCard>

            <KCard className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold flex items-center gap-2"><FileText size={18} /> Sections</h3>
                <Button size="sm" onClick={() => setEditSection({ status: 'ACTIVE', sortOrder: (activeConfig.sections?.length || 0) + 1 })}>
                  <Plus size={14} className="mr-1" /> Add Section
                </Button>
              </div>
              <div className="space-y-2">
                {activeConfig.sections?.sort((a, b) => a.sortOrder - b.sortOrder).map(sec => (
                  <div key={sec.id} className="flex items-center justify-between p-3 border border-border rounded-lg bg-card">
                    <div>
                      <p className="font-medium text-sm">{sec.name} <span className="text-muted-foreground text-xs">({sec.code})</span></p>
                      {sec.japaneseName && <p className="text-xs text-muted-foreground">{sec.japaneseName}</p>}
                    </div>
                    <div className="flex gap-1"><Button variant="ghost" size="sm" onClick={() => setEditSection(sec)}><Pencil size={14} /></Button><Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleCheckDelete('SECTION', sec)}><Trash2 size={14} /></Button></div>
                  </div>
                ))}
              </div>
            </KCard>
          </div>
        </div>

        {/* Level Editor Dialog */}
        <Dialog open={!!editLevel} onOpenChange={(o) => !o && setEditLevel(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editLevel?.id ? 'Edit Level' : 'Add Level'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Code (e.g. N5)</Label><Input value={editLevel?.code || ''} onChange={e => setEditLevel(prev => ({ ...prev!, code: e.target.value }))} /></div>
              <div><Label>Name</Label><Input value={editLevel?.name || ''} onChange={e => setEditLevel(prev => ({ ...prev!, name: e.target.value }))} /></div>
              <div><Label>Sort Order</Label><Input type="number" value={editLevel?.sortOrder || 0} onChange={e => setEditLevel(prev => ({ ...prev!, sortOrder: Number(e.target.value) }))} /></div>
              <div>
                <Label>Status</Label>
                <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editLevel?.status} onChange={e => setEditLevel(prev => ({ ...prev!, status: e.target.value as any }))}>
                  <option value="ACTIVE">Active</option><option value="INACTIVE">Inactive</option><option value="ARCHIVED">Archived</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditLevel(null)}>Cancel</Button>
              <Button onClick={() => levelMutation.mutate(editLevel!)} disabled={levelMutation.isPending}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Section Editor Dialog */}
        <Dialog open={!!editSection} onOpenChange={(o) => !o && setEditSection(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editSection?.id ? 'Edit Section' : 'Add Section'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Code (e.g. VOCABULARY)</Label><Input value={editSection?.code || ''} onChange={e => setEditSection(prev => ({ ...prev!, code: e.target.value }))} /></div>
              <div><Label>English Name</Label><Input value={editSection?.name || ''} onChange={e => setEditSection(prev => ({ ...prev!, name: e.target.value }))} /></div>
              <div><Label>Japanese Name</Label><Input value={editSection?.japaneseName || ''} onChange={e => setEditSection(prev => ({ ...prev!, japaneseName: e.target.value }))} /></div>
              <div><Label>Sort Order</Label><Input type="number" value={editSection?.sortOrder || 0} onChange={e => setEditSection(prev => ({ ...prev!, sortOrder: Number(e.target.value) }))} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditSection(null)}>Cancel</Button>
              <Button onClick={() => sectionMutation.mutate(editSection!)} disabled={sectionMutation.isPending}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Protection Modal */}
        <Dialog open={!!deleteUsage} onOpenChange={(o) => !o && setDeleteUsage(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Deletion</DialogTitle>
              <DialogDescription>
                You are about to delete <strong>{deleteUsage?.item?.name}</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {deleteUsage?.usage?.totalDependencies > 0 ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Cannot Delete: Entity in Use</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                      {deleteUsage.usage.courses > 0 && <li>{deleteUsage.usage.courses} Courses</li>}
                      {deleteUsage.usage.courseExams > 0 && <li>{deleteUsage.usage.courseExams} Course Exams</li>}
                      {deleteUsage.usage.mockTests > 0 && <li>{deleteUsage.usage.mockTests} Mock Tests</li>}
                      {deleteUsage.usage.studentAttempts > 0 && <li>{deleteUsage.usage.studentAttempts} Student Attempts</li>}
                      {deleteUsage.usage.questions > 0 && <li>{deleteUsage.usage.questions} Course Questions</li>}
                      {deleteUsage.usage.mockTestQuestions > 0 && <li>{deleteUsage.usage.mockTestQuestions} Mock Test Questions</li>}
                    </ul>
                    <p className="mt-2 font-medium">To preserve historical records, please Deactivate or Archive this entity instead.</p>
                  </AlertDescription>
                </Alert>
              ) : (
                <p>This entity is not currently used by any courses, mock tests, or questions. It is safe to delete.</p>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setDeleteUsage(null)}>Cancel</Button>
              {deleteUsage?.usage?.totalDependencies > 0 ? (
                <>
                  <Button variant="secondary" onClick={() => handleDeactivateArchive('INACTIVE')} disabled={isDeleting}>Deactivate</Button>
                  <Button variant="secondary" onClick={() => handleDeactivateArchive('ARCHIVED')} disabled={isDeleting}>Archive</Button>
                </>
              ) : (
                <Button variant="destructive" onClick={executeDelete} disabled={isDeleting}>Hard Delete</Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="dash-page max-w-5xl mx-auto">
        <div className="dash-page-header">
          <div>
            <h1 className="dash-page-title">Exam Configurations</h1>
            <p className="dash-page-subtitle">Manage dynamic exam types, levels, and sections</p>
          </div>
          <Button onClick={() => setEditConfig({ status: 'ACTIVE', passCriteria: 'SECTIONAL_REQUIRED', sortOrder: configs.length + 1 })}>
            <Plus size={16} className="mr-2" /> New Exam Type
          </Button>
        </div>

        {isLoading ? (
          <div className="dash-empty-state">
            <Loader2 className="animate-spin mb-2" size={24} />
            <span className="text-sm font-medium">Loading configurations...</span>
          </div>
        ) : configs.length === 0 ? (
          <div className="dash-empty-state">
            <Settings className="text-muted-foreground/30 mb-2" size={32} />
            <p className="text-sm font-medium mb-4">No exam configurations found</p>
            <Button size="sm" variant="outline" onClick={() => setEditConfig({ status: 'ACTIVE', passCriteria: 'SECTIONAL_REQUIRED', sortOrder: 1 })}>
              <Plus size={13} className="mr-1" /> Create your first configuration
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {configs.map((config) => (
              <KCard key={config.id} className="p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:border-primary/30 transition-colors">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-lg font-bold">{config.name}</h2>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">{config.code}</span>
                    {config.status !== 'ACTIVE' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-destructive/10 text-destructive">{config.status}</span>}
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{config.description}</p>
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span><strong>{config.levels?.length || 0}</strong> Levels</span>
                    <span><strong>{config.sections?.length || 0}</strong> Sections</span>
                    <span>Pass Criteria: {config.passCriteria}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setEditConfig(config)}><Settings size={14} className="mr-2"/> Edit</Button>
                  <Button onClick={() => setActiveConfig(config)}>Manage <ChevronRight size={14} className="ml-1" /></Button>
                </div>
              </KCard>
            ))}
          </div>
        )}

        <Dialog open={!!editConfig} onOpenChange={(o) => !o && setEditConfig(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editConfig?.id ? 'Edit Exam Type' : 'New Exam Type'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Code (e.g. JLPT)</Label><Input value={editConfig?.code || ''} onChange={e => setEditConfig(prev => ({ ...prev!, code: e.target.value }))} disabled={!!editConfig?.id} /></div>
              <div><Label>Name</Label><Input value={editConfig?.name || ''} onChange={e => setEditConfig(prev => ({ ...prev!, name: e.target.value }))} /></div>
              <div><Label>Description</Label><Input value={editConfig?.description || ''} onChange={e => setEditConfig(prev => ({ ...prev!, description: e.target.value }))} /></div>
              <div>
                <Label>Mock Test Rules (Rich Text)</Label>
                <div className="mt-1 [&_.ql-container]:min-h-[150px] [&_.ql-editor]:text-sm">
                  <ReactQuill 
                    theme="snow" 
                    value={editConfig?.mockTestRules || ''} 
                    onChange={val => setEditConfig(prev => prev ? { ...prev, mockTestRules: val } : null)} 
                    modules={quillModules}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pass Criteria</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editConfig?.passCriteria} onChange={e => setEditConfig(prev => ({ ...prev!, passCriteria: e.target.value as any }))}>
                    <option value="SECTIONAL_REQUIRED">Overall + Sectional</option>
                    <option value="OVERALL_ONLY">Overall Only</option>
                  </select>
                </div>
                <div>
                  <Label>Status</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editConfig?.status} onChange={e => setEditConfig(prev => ({ ...prev!, status: e.target.value as any }))}>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="ARCHIVED">Archived</option>
                  </select>
                </div>
              </div>
            </div>
            <DialogFooter>
              {editConfig?.id && (
                <Button variant="ghost" className="text-destructive mr-auto" onClick={(e) => { e.preventDefault(); handleCheckDelete('CONFIG', editConfig); }}>Delete / Usage</Button>
              )}
              <Button variant="outline" onClick={() => setEditConfig(null)}>Cancel</Button>
              <Button onClick={() => configMutation.mutate(editConfig!)} disabled={configMutation.isPending}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Delete Protection Modal */}
        <Dialog open={!!deleteUsage} onOpenChange={(o) => !o && setDeleteUsage(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Deletion</DialogTitle>
              <DialogDescription>
                You are about to delete <strong>{deleteUsage?.item?.name}</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {deleteUsage?.usage?.totalDependencies > 0 ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Cannot Delete: Entity in Use</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                      {deleteUsage.usage.courses > 0 && <li>{deleteUsage.usage.courses} Courses</li>}
                      {deleteUsage.usage.courseExams > 0 && <li>{deleteUsage.usage.courseExams} Course Exams</li>}
                      {deleteUsage.usage.mockTests > 0 && <li>{deleteUsage.usage.mockTests} Mock Tests</li>}
                      {deleteUsage.usage.studentAttempts > 0 && <li>{deleteUsage.usage.studentAttempts} Student Attempts</li>}
                      {deleteUsage.usage.questions > 0 && <li>{deleteUsage.usage.questions} Course Questions</li>}
                      {deleteUsage.usage.mockTestQuestions > 0 && <li>{deleteUsage.usage.mockTestQuestions} Mock Test Questions</li>}
                    </ul>
                    <p className="mt-2 font-medium">To preserve historical records, please Deactivate or Archive this entity instead.</p>
                  </AlertDescription>
                </Alert>
              ) : (
                <p>This entity is not currently used by any courses, mock tests, or questions. It is safe to delete.</p>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setDeleteUsage(null)}>Cancel</Button>
              {deleteUsage?.usage?.totalDependencies > 0 ? (
                <>
                  <Button variant="secondary" onClick={() => handleDeactivateArchive('INACTIVE')} disabled={isDeleting}>Deactivate</Button>
                  <Button variant="secondary" onClick={() => handleDeactivateArchive('ARCHIVED')} disabled={isDeleting}>Archive</Button>
                </>
              ) : (
                <Button variant="destructive" onClick={executeDelete} disabled={isDeleting}>Hard Delete</Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
}
